import Close from '@mui/icons-material/Close';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { ReactNode, useEffect, useRef } from 'react';

import useTreadAgentConversationsQuery from '~queries/useTreadAgentConversationsQuery';
import TreadAgentLoader from '~tread-agent/TreadAgentLoader';
import { TreadAgentTab } from '~tread-agent/treadAgentTabState';

const DOCKED_TAB_MAX_WIDTH_IN_PX = 200;
const DOCKED_TAB_MIN_WIDTH_IN_PX = 140;
const EXPANDED_TAB_WIDTH_IN_PX = 180;
const TITLE_FADE_WIDTH_IN_PX = 14;

interface TreadAgentTabItemProps {
  isActive: boolean;
  isWorking: boolean;
  onClose: () => void;
  onSelect: () => void;
  tab: TreadAgentTab;
  title: string | null | undefined;
  variant: 'docked' | 'expanded';
}

function TreadAgentTabItem(props: TreadAgentTabItemProps) {
  const { isActive, isWorking, onClose, onSelect, tab, title, variant } = props;
  const isDocked = variant === 'docked';
  const theme = useTheme();
  const tabRef = useRef<HTMLDivElement>(null);
  const label = tab.conversationId
    ? title || t('tread_agent.untitled_chat')
    : t('tread_agent.new_chat');

  useEffect(() => {
    if (isActive) {
      tabRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [isActive]);

  return (
    <Box
      ref={tabRef}
      data-test-id="tread-agent-tab"
      sx={{
        alignItems: 'center',
        backgroundColor: isActive ? theme.brandV2.colors.treadGray8 : 'transparent',
        borderRadius: theme.brandV2.borderRadius,
        display: 'flex',
        '&:hover': { backgroundColor: theme.brandV2.colors.treadGray8 },
        ...(isDocked
          ? {
              flexShrink: 1,
              maxWidth: `${DOCKED_TAB_MAX_WIDTH_IN_PX}px`,
              minWidth: `${DOCKED_TAB_MIN_WIDTH_IN_PX}px`,
              position: 'relative',
              px: 1.5,
              '&:hover .tread-agent-tab-close, & .tread-agent-tab-close:focus-visible': {
                opacity: 1,
              },
            }
          : {
              flexShrink: 0,
              pl: 1.5,
              pr: 0.5,
              width: `${EXPANDED_TAB_WIDTH_IN_PX}px`,
            }),
      }}
    >
      <ButtonBase
        aria-selected={isActive}
        onClick={onSelect}
        role="tab"
        sx={{
          flexGrow: 1,
          gap: 0.75,
          justifyContent: 'flex-start',
          minWidth: 0,
          py: 0.75,
          textAlign: 'left',
        }}
      >
        {isWorking && <TreadAgentLoader size={14} />}

        <Typography
          noWrap
          sx={{
            color: isActive ? 'text.primary' : theme.brandV2.colors.treadGray3,
            fontSize: '12px',
            fontWeight: 500,
            ...(isDocked && {
              maskImage: `linear-gradient(to right, black calc(100% - ${TITLE_FADE_WIDTH_IN_PX}px), transparent)`,
              pr: `${TITLE_FADE_WIDTH_IN_PX}px`,
              textOverflow: 'clip',
            }),
          }}
        >
          {label}
        </Typography>
      </ButtonBase>

      <IconButton
        aria-label={t('tread_agent.close_tab', { title: label })}
        className="tread-agent-tab-close"
        onClick={onClose}
        size="small"
        sx={
          isDocked
            ? {
                backgroundColor: theme.brandV2.colors.treadGray8,
                opacity: 0,
                p: 0.25,
                position: 'absolute',
                right: 6,
                '&:hover': { backgroundColor: theme.brandV2.colors.treadGray7 },
              }
            : { ml: 1, p: 0.25 }
        }
      >
        <Close sx={{ fontSize: 12 }} />
      </IconButton>
    </Box>
  );
}

interface TreadAgentTabsProps {
  companyId: string;
  isTabActive: (tab: TreadAgentTab) => boolean;
  onCloseTab: (tab: TreadAgentTab) => void;
  onSelectTab: (tab: TreadAgentTab) => void;
  tabs: TreadAgentTab[];
  workingConversationId: string | null;
  variant: 'docked' | 'expanded';
}

export type ExpandedTabStripRenderer = (state: {
  isSkillsViewOpen: boolean;
  onLeaveSkillsView: () => void;
}) => ReactNode;

export default function TreadAgentTabs(props: TreadAgentTabsProps) {
  const {
    companyId,
    isTabActive,
    onCloseTab,
    onSelectTab,
    tabs,
    variant,
    workingConversationId,
  } = props;
  const { conversations } = useTreadAgentConversationsQuery(companyId, {
    enabled: tabs.length > 0,
  });

  return (
    <Box
      aria-label={t('tread_agent.open_chats')}
      role="tablist"
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexGrow: variant === 'expanded' ? 1 : 0,
        gap: 1,
        minWidth: 0,
        mr: variant === 'docked' ? 1 : 0,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {tabs.map((tab) => (
        <TreadAgentTabItem
          key={tab.conversationId ?? 'draft'}
          isActive={isTabActive(tab)}
          isWorking={
            tab.conversationId !== null && tab.conversationId === workingConversationId
          }
          onClose={() => onCloseTab(tab)}
          onSelect={() => onSelectTab(tab)}
          tab={tab}
          title={conversations.find(({ id }) => id === tab.conversationId)?.title}
          variant={variant}
        />
      ))}
    </Box>
  );
}
